import { Component, ojs, app, state, v } from "modular-openscriptjs";
import { pageInfo, inform, scrollToTop, consoleErrorOnLocal, store } from "../../../helpers";
import { api, STRINGS } from "../../../declarations";
import { dom } from "../../../fotastart";
import SolidIcon from "../../Blocks/SolidIcon";

const h = app("h");
const router = app("router");

ojs(SolidIcon);

// Monday-first for display, but the "value" is Carbon's dayOfWeek numbering
// (0 = Sunday .. 6 = Saturday), which is also what the API stores/expects -
// see App\Enums\DayOfWeek on the backend.
export const DAYS = [
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
    { value: 0, label: "Sunday" },
];

const DEFAULT_SLOT = () => ({ opens_at: "09:00", closes_at: "17:00" });

// Icon-only on small screens (title attr covers accessibility/a tooltip
// there), icon + label from md up - these three buttons were overflowing
// small screens when they always showed full text.
function renderApplyButton(icon, label, onClick) {
    return h.button(
        {
            class: "btn btn-sm btn-light border rounded-pill px-2 px-md-3",
            title: label,
            listeners: { click: onClick },
        },
        h.i({ class: `bi bi-${icon}` }),
        h.span({ class: "d-none d-md-inline ms-1" }, label),
    );
}

export function emptySchedule() {
    const schedule = {};
    for (const day of DAYS) schedule[day.value] = [];
    return schedule;
}

export function scheduleFromTimeSlots(timeSlots = []) {
    const schedule = emptySchedule();
    for (const slot of timeSlots) {
        const key = slot.day_of_week;
        if (!schedule[key]) schedule[key] = [];
        schedule[key].push({
            opens_at: (slot.opens_at || "").slice(0, 5),
            closes_at: (slot.closes_at || "").slice(0, 5),
        });
    }
    return schedule;
}

export function scheduleToTimeSlots(schedule) {
    const timeSlots = [];
    for (const day of DAYS) {
        for (const slot of schedule[day.value] || []) {
            timeSlots.push({
                day_of_week: day.value,
                opens_at: slot.opens_at,
                closes_at: slot.closes_at,
            });
        }
    }
    return timeSlots;
}

/**
 * Renders the Monday-Sunday availability editor (day cards, add/remove
 * slot, apply-to-all/above/below) shared by the company-level Manage
 * Availability page and the branch "Availability" tab. `scheduleState` is
 * a state() holding a { [dayValue]: [{opens_at, closes_at}] } map - the
 * caller owns fetching/saving it, this just renders it and mutates it via
 * scheduleState.value = { ...new object... } (state objects only fire
 * listeners on reference replacement, never on in-place mutation).
 */
export function renderAvailabilityEditor(scheduleState) {
    const setSchedule = (next) => (scheduleState.value = next);

    const addSlot = (dayValue) => {
        const current = scheduleState.value;
        setSchedule({
            ...current,
            [dayValue]: [...(current[dayValue] || []), DEFAULT_SLOT()],
        });
    };

    const removeSlot = (dayValue, index) => {
        const current = scheduleState.value;
        setSchedule({
            ...current,
            [dayValue]: current[dayValue].filter((_, i) => i !== index),
        });
    };

    const updateSlot = (dayValue, index, field, value) => {
        const current = scheduleState.value;
        setSchedule({
            ...current,
            [dayValue]: current[dayValue].map((slot, i) =>
                i === index ? { ...slot, [field]: value } : slot,
            ),
        });
    };

    const cloneSlots = (dayValue) =>
        (scheduleState.value[dayValue] || []).map((slot) => ({ ...slot }));

    const applyTo = (targetDayValues, sourceDayValue) => {
        const source = cloneSlots(sourceDayValue);
        const current = scheduleState.value;
        const next = { ...current };
        for (const dayValue of targetDayValues) {
            next[dayValue] = source.map((slot) => ({ ...slot }));
        }
        setSchedule(next);
        inform("Applied to selected days. Remember to save.", STRINGS.INFO);
    };

    const applyToAll = (dayValue) =>
        applyTo(
            DAYS.map((d) => d.value).filter((v) => v !== dayValue),
            dayValue,
        );

    const applyAbove = (dayValue) => {
        const index = DAYS.findIndex((d) => d.value === dayValue);
        applyTo(DAYS.slice(0, index).map((d) => d.value), dayValue);
    };

    const applyBelow = (dayValue) => {
        const index = DAYS.findIndex((d) => d.value === dayValue);
        applyTo(DAYS.slice(index + 1).map((d) => d.value), dayValue);
    };

    // The day cards need to be direct children of the "d-grid gap-3"
    // element for the grid gap to actually show - v()'s own wrapper
    // element is that direct-children container (via c_attr), NOT the
    // outer call site, since a fragment returned from the render callback
    // doesn't add a node of its own (the cards would otherwise all end up
    // nested one level too deep, inside a single non-grid child, and the
    // gap would have nothing to apply between).
    return v(
        scheduleState,
        (s) =>
            h.div(
                { class: "d-grid gap-3" },
                ...DAYS.map((day, dayIndex) => {
                    const slots = s.value[day.value] || [];
                    const closed = slots.length === 0;

                    return h.div(
                        { class: "card border-0 shadow-sm rounded-4" },
                        h.div(
                            { class: "card-body p-4" },
                            h.div(
                                { class: "d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3" },
                                h.div(
                                    { class: "d-flex align-items-center gap-2" },
                                    h.h6({ class: "fw-bold mb-0" }, day.label),
                                    closed
                                        ? h.span(
                                              { class: "badge bg-secondary-subtle text-secondary rounded-pill fw-normal" },
                                              "Closed",
                                          )
                                        : h.span(
                                              { class: "badge bg-success-subtle text-success rounded-pill fw-normal" },
                                              `${slots.length} time slot${slots.length > 1 ? "s" : ""}`,
                                          ),
                                ),
                                h.div(
                                    { class: "d-flex flex-wrap gap-2" },
                                    // No day above Monday, and none below Sunday, to apply to.
                                    dayIndex > 0
                                        ? renderApplyButton("arrow-up", "Apply to days above", () =>
                                              applyAbove(day.value),
                                          )
                                        : null,
                                    dayIndex < DAYS.length - 1
                                        ? renderApplyButton("arrow-down", "Apply to days below", () =>
                                              applyBelow(day.value),
                                          )
                                        : null,
                                    renderApplyButton("copy", "Apply to all days", () =>
                                        applyToAll(day.value),
                                    ),
                                ),
                            ),

                            h.div(
                                { class: "d-grid gap-2 mb-3" },
                                ...slots.map((slot, index) =>
                                    h.div(
                                        { class: "d-flex align-items-center gap-1 gap-sm-2 flex-nowrap justify-content-between" },
                                        h.input({
                                            type: "time",
                                            class: "form-control form-control-sm px-1 bg-light border flex-shrink-1 rounded-3",
                                            style: "max-width: 105px; min-width: 0;",
                                            value: slot.opens_at,
                                            listeners: {
                                                change: (e) =>
                                                    updateSlot(day.value, index, "opens_at", e.target.value),
                                            },
                                        }),
                                        h.span({ class: "text-muted small flex-shrink-0" }, "to"),
                                        h.input({
                                            type: "time",
                                            class: "form-control form-control-sm px-1 bg-light border flex-shrink-1 rounded-3",
                                            style: "max-width: 105px; min-width: 0;",
                                            value: slot.closes_at,
                                            listeners: {
                                                change: (e) =>
                                                    updateSlot(day.value, index, "closes_at", e.target.value),
                                            },
                                        }),
                                        h.button(
                                            {
                                                class: "btn btn-outline-danger btn-sm rounded-circle flex-shrink-0",
                                                style: "width: 28px; height: 28px; padding: 0;",
                                                listeners: { click: () => removeSlot(day.value, index) },
                                            },
                                            h.i({ class: "bi bi-trash small" }),
                                        ),
                                    ),
                                ),
                            ),

                            h.button(
                                {
                                    class: "btn btn-outline-primary btn-sm rounded-pill px-3",
                                    listeners: { click: () => addSlot(day.value) },
                                },
                                h.i({ class: "bi bi-plus-lg me-1" }),
                                "Add time slot",
                            ),
                        ),
                    );
                }),
            ),
        { c_attr: { class: "d-grid gap-3" } },
    );
}

export default class ManageAvailability extends Component {
    businessId = null;
    loading = state(true);
    schedule = state(emptySchedule());

    render() {
        this.businessId = router.params.companyId;
        scrollToTop();
        pageInfo("Manage Availability");

        if (this.loading.value) {
            this.fetchAvailability();
        }

        return h.div(
            { class: "min-vh-100 py-5 mb-5 py-md-5" },
            h.div(
                { class: "container max-w-800" },
                h.div(
                    { class: "mb-4 d-flex align-items-center gap-3" },
                    h.button(
                        { class: "btn btn-white border shadow-sm rounded-circle p-2", onclick: "history.back()" },
                        h.SolidIcon("arrow-left"),
                    ),
                    h.div(
                        h.h2({ class: "fw-bold mb-0" }, "Manage Availability"),
                        h.p({ class: "text-muted mb-0" }, "Set your weekly opening hours."),
                    ),
                ),

                v(this.loading, (l) => {
                    if (l.value) return h.div({ class: "text-center py-5" }, h.Spinner());

                    return h.$(
                        renderAvailabilityEditor(this.schedule),
                        h.div(
                            { class: "text-end mt-4" },
                            h.button(
                                {
                                    class: "btn btn-primary rounded-pill px-5 py-3 fw-bold shadow-sm",
                                    id: `save-availability-${this.id}`,
                                    listeners: { click: (e) => this.saveAvailability(e.target) },
                                },
                                "Save Availability",
                            ),
                        ),
                    );
                }),
            ),
        );
    }

    async fetchAvailability() {
        try {
            const res = await api.get(`companies/${this.businessId}/availability`);
            if (res) {
                const timeSlots = res.data.availability?.time_slots || [];
                this.schedule.value = scheduleFromTimeSlots(timeSlots);
            }
        } catch (e) {
            consoleErrorOnLocal(e);
        } finally {
            this.loading.value = false;
        }
    }

    async saveAvailability(btn) {
        try {
            dom.disable(btn);
            h.Spinner({ parent: btn, resetParent: true });

            const res = await api.put(`companies/${this.businessId}/availability`, {
                time_slots: scheduleToTimeSlots(this.schedule.value),
            });

            if (res) {
                inform("Availability updated successfully!", STRINGS.SUCCESS);

                // Keep window.currentCompany in sync so the "has this
                // company set its hours" onboarding gate (see
                // Middleware::availability()) clears immediately on the
                // very next navigation, instead of only after a re-login.
                if (window.currentCompany?.id == this.businessId) {
                    window.currentCompany.availability = res.data.availability;
                    store("current_company", window.currentCompany);
                }
            }
        } catch (e) {
            consoleErrorOnLocal(e);
            inform("Failed to update availability", STRINGS.ERROR);
        } finally {
            dom.enable(btn);
            h.span("Save Availability", { parent: btn, resetParent: true });
        }
    }
}

ojs(ManageAvailability);
